import { PORTAL_HUB_CLIENT_EVENTS } from '@/signalr';
import type { IncidentId, PortalHubClientEvents } from '@/signalr';

export type PortalHubEventName = keyof PortalHubClientEvents & string;

export type RecordedEventSource = 'hub' | 'connection';

export type RecordedHubEvent = {
  readonly sequence: number;
  readonly receivedAt: Date;
  readonly source: RecordedEventSource;
  readonly name: string;
  readonly incidentId: IncidentId | null;
  readonly summary: string;
  readonly args: readonly unknown[];
};

export type RecordedEventFilter = {
  readonly name: string;
  readonly text: string;
};

export const PORTAL_HUB_EVENT_NAMES = Object.keys(PORTAL_HUB_CLIENT_EVENTS) as PortalHubEventName[];

export const HUB_CONNECTION_EVENT_NAMES = ['HubReconnecting', 'HubReconnected', 'HubClosed'] as const;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SUMMARY_LENGTH = 240;

function jsonSafe(_key: string, value: unknown): unknown {
  if (value instanceof Map) {
    return Object.fromEntries(value);
  }
  if (value instanceof Set) {
    return [...value];
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }

  return value;
}

export function stringifyForDisplay(value: unknown, indent = 2): string {
  try {
    return JSON.stringify(value, jsonSafe, indent) ?? String(value);
  } catch (error) {
    return `<not serialisable: ${String(error)}>`;
  }
}

export function incidentIdOfEvent(name: string, args: readonly unknown[]): IncidentId | null {
  const contracts: Record<string, { readonly params: readonly string[] } | undefined> = PORTAL_HUB_CLIENT_EVENTS;
  const firstParam = contracts[name]?.params[0];
  const first = args[0];

  if (firstParam === 'IncidentId') {
    return typeof first === 'string' && GUID.test(first) ? first : null;
  }

  if (firstParam === 'PortalIncident') {
    const incidentId = (first as { incidentId?: unknown } | null | undefined)?.incidentId;
    return typeof incidentId === 'string' && GUID.test(incidentId) ? incidentId : null;
  }

  return null;
}

export function summariseEvent(name: string, args: readonly unknown[]): string {
  const incidentId = incidentIdOfEvent(name, args);
  const payload = incidentId !== null && args[0] === incidentId ? args.slice(1) : [...args];
  if (payload.length === 0) {
    return '';
  }

  const text = stringifyForDisplay(payload.length === 1 ? payload[0] : payload, 0);
  return text.length > SUMMARY_LENGTH ? `${text.slice(0, SUMMARY_LENGTH)}…` : text;
}

export function toRecordedEvent(
  sequence: number,
  source: RecordedEventSource,
  name: string,
  args: readonly unknown[],
  receivedAt: Date,
): RecordedHubEvent {
  return {
    sequence,
    receivedAt,
    source,
    name,
    incidentId: incidentIdOfEvent(name, args),
    summary: summariseEvent(name, args),
    args,
  };
}

export function appendRecordedEvent(
  events: readonly RecordedHubEvent[],
  event: RecordedHubEvent,
  capacity: number,
): readonly RecordedHubEvent[] {
  const next = [event, ...events];
  return next.length > capacity ? next.slice(0, capacity) : next;
}

export function filterRecordedEvents(
  events: readonly RecordedHubEvent[],
  filter: RecordedEventFilter,
): readonly RecordedHubEvent[] {
  const text = filter.text.trim().toLowerCase();

  return events.filter((event) => {
    if (filter.name !== '' && event.name !== filter.name) {
      return false;
    }
    if (text === '') {
      return true;
    }

    return `${event.name} ${event.incidentId ?? ''} ${event.summary}`.toLowerCase().includes(text);
  });
}

export function formatEventTime(receivedAt: Date): string {
  const time = receivedAt.toLocaleTimeString('en-GB', { hour12: false });
  return `${time}.${String(receivedAt.getMilliseconds()).padStart(3, '0')}`;
}
